import {
  Component,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnInit,
  OnDestroy,
  Inject,
  PLATFORM_ID,
  ChangeDetectorRef,
  Renderer2,
} from "@angular/core";
import { CommonModule, isPlatformBrowser, DOCUMENT } from "@angular/common";
import { RouterModule, Router } from "@angular/router";
import Lenis from "lenis";
import { HugeiconsIconComponent } from "@hugeicons/angular";
import {
  ArrowLeft02Icon,
  ArrowRight02Icon,
  Search02Icon,
  PlusSignCircleIcon,
  MinusSignCircleIcon,
  Pdf01Icon,
  Loading03Icon,
} from "@hugeicons/core-free-icons";
import { SupabaseService } from "../../services/supabase.service";
import { AuthService } from "../../services/auth.service";
import { Job, WorkType } from "../../types/job.types";
import { filter, take, skip } from "rxjs/operators";
import { JobMatcherAnimationComponent } from "../../components/job-matcher-animation/job-matcher-animation.component";
import { ModalComponent } from "../../shared/components/modal/modal.component";
import { ResumeResultsModalComponent } from "../../components/resume-analyzer/resume-results-modal.component";
import type {
  ResumeAnalysisResult,
  ActionItem,
  TipItem,
} from "../../services/resume-job-analyzer.service";
import { SeoService } from "../../services/seo.service";

declare let posthog: any;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

@Component({
  selector: "app-homepage-new",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HugeiconsIconComponent,
    JobMatcherAnimationComponent,
    ModalComponent,
    ResumeResultsModalComponent,
  ],
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.scss"],
})
export class HomepageNewComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
  private static featuredCompaniesCache: CacheEntry<
    Array<{ company: string; logo_img: string | null }>
  > | null = null;
  private static hiringAnnouncementsCache: CacheEntry<
    Array<{
      id: string;
      company: string;
      logo_img: string;
      category: string;
      title: string;
    }>
  > | null = null;
  private static blogPostsCache: CacheEntry<any[]> | null = null;
  private static jobsByCategoryCache = new Map<string, CacheEntry<Job[]>>();

  private static isCacheValid<T>(cache: CacheEntry<T> | null): boolean {
    if (!cache) return false;
    return Date.now() - cache.timestamp < HomepageNewComponent.CACHE_TTL_MS;
  }

  /**
   * Invalidate all home page caches (call after job posting/updates)
   */
  static invalidateCache(): void {
    HomepageNewComponent.featuredCompaniesCache = null;
    HomepageNewComponent.hiringAnnouncementsCache = null;
    HomepageNewComponent.jobsByCategoryCache.clear();
    // Don't clear blog posts cache - not affected by job changes
  }

  icons = {
    arrowLeft: ArrowLeft02Icon,
    arrowRight: ArrowRight02Icon,
    search: Search02Icon,
    plus: PlusSignCircleIcon,
    minus: MinusSignCircleIcon,
    pdf: Pdf01Icon,
    loading: Loading03Icon,
  };
  @ViewChild("toolkitCardsScroll") toolkitCardsScroll!: ElementRef<HTMLDivElement>;
  @ViewChild("toolkitCardsGrid") toolkitCardsGrid!: ElementRef<HTMLDivElement>;
  @ViewChild("insightsCardsScroll") insightsCardsScroll!: ElementRef<HTMLDivElement>;
  @ViewChild("insightsCardsGrid") insightsCardsGrid!: ElementRef<HTMLDivElement>;
  @ViewChild("categoriesNav") categoriesNavRef!: ElementRef<HTMLElement>;
  @ViewChild("resumeAnalyserVideo") resumeAnalyserVideo?: ElementRef<HTMLVideoElement>;

  private lenisCarousel: Lenis | null = null;
  private readonly scrollStep = 338;
  /** Used in template for SSR-safe collapse (e.g. founders note). */
  isBrowser: boolean;
  animatingCategory: string | null = null;

  /** Toolkit carousel: disable nav when at start/end */
  toolkitCanScrollLeft = true;
  toolkitCanScrollRight = true;
  private toolkitScrollListener: (() => void) | null = null;
  /** Insights carousel: disable nav when at start/end */
  insightsCanScrollLeft = false;
  insightsCanScrollRight = true;
  private insightsScrollListener: (() => void) | null = null;
  private resizeListener: (() => void) | null = null;
  private categoryIconAnimationTimer: ReturnType<typeof setTimeout> | null = null;

  // Design Ecosystem categories (active = first)
  selectedCategory = 'Product Design';
  categories = [
    {
      id: 'product-design',
      name: 'Product Design',
      icon: 'assets/svgs/homepage/product-design.svg',
    },
    {
      id: 'ui-ux-design',
      name: 'UI/UX Design',
      icon: 'assets/svgs/homepage/uiux-design.svg',
    },
    {
      id: 'ux-research',
      name: 'UX Research',
      icon: 'assets/svgs/homepage/ux-research.svg',
    },
    {
      id: 'graphic-design',
      name: 'Graphic Design',
      icon: 'assets/svgs/homepage/graphic-design.svg',
    },
    {
      id: 'design-engineering',
      name: 'Design Engineering',
      icon: 'assets/svgs/homepage/design-eng.svg',
    },
    {
      id: 'web-design',
      name: 'Web Design',
      icon: 'assets/svgs/homepage/web-design.svg',
    },
    {
      id: 'motion-design',
      name: 'Motion Design',
      icon: 'assets/svgs/homepage/motion-design.svg',
    },
  ];

  // Job data
  jobs: Job[] = [];
  isLoadingJobs = false;
  jobsError: string | null = null;
  private jobsByCategory = new Map<string, Job[]>();

  // Skeleton array for loading state
  skeletonJobs = Array(12).fill(null);

  // Featured companies for "Top teams hiring now"
  featuredCompanies: Array<{ company: string; logo_img: string | null }> = [];
  isLoadingCompanies = false;

  // Hiring announcements for infinite scroll marquee
  hiringAnnouncements: Array<{
    id: string;
    company: string;
    logo_img: string | null;
    category: string;
    cleanTitle: string;
  }> = [];

  // Founder's note expandable content
  foundersNoteExpanded = false;

  private founderSchemaScript: HTMLScriptElement | null = null;

  // FAQ expandable content
  expandedFaqs = new Set<number>();

  private faqSchemaScript: HTMLScriptElement | null = null;

  // Blog posts
  blogPosts: any[] = [];
  isBlogsLoading = false;

  // FAQ data (single source of truth); answers may contain HTML links for SEO/internal linking
  faqs = [
    {
      question: "Is DesignJobs.careers a legitimate platform for designers?",
      answer:
        'Yes. We are a specialized career engine built specifically for the design industry. Unlike generalist job boards, we curate roles for Product, UX, and Creative specialists at high-growth companies like OpenAI, Vercel, and Stripe. <a href="/about" class="underline font-semibold">Learn more about us</a>.'
    },
    {
      question: "What types of design roles are available?",
      answer:
        'We cover the full spectrum of modern design: <a href="/jobs/product-design" class="underline font-semibold">Product Design</a>, <a href="/jobs/ux-research" class="underline font-semibold">UX Research</a>, <a href="/jobs/graphic-design" class="underline font-semibold">Graphic Design</a>, <a href="/jobs/motion-design" class="underline font-semibold">Motion Design</a>, <a href="/jobs/design-engineering" class="underline font-semibold">Design Engineering</a>, and more. We prioritize roles at design-led organizations where your craft is recognized as a core business driver.'
    },
    {
      question: "Why should I hire through this platform instead of LinkedIn?",
      answer:
        'Quality over quantity. Generalist boards often result in thousands of unqualified applications that drain your hiring team\'s time. DesignJobs.careers provides a niche-only audience of high-intent professionals. By focusing exclusively on the design community, we help you skip the noise and connect with candidates who truly understand the builder mindset. <a href="/post-job" class="underline font-semibold">Post a role</a> or <a href="/hire-designers" class="underline font-semibold">see our hire-designers page for employers</a>.'
    },
    {
      question: "Can I find remote design jobs here?",
      answer:
        'Absolutely. Over 40% of the 4,500+ roles on our platform are Remote or Hybrid. <a href="/jobs/remote" class="underline font-semibold">Browse remote design jobs</a> or filter by time zones and global-remote status to find teams that support a flexible, high-autonomy design workflow.'
    },
    {
      question: "Is DesignJobs.careers free for job seekers?",
      answer:
        'Yes. Browsing and applying is 100% free. You can <a href="/jobs" class="underline font-semibold">browse jobs</a>, use our <a href="/ai-job-matcher" class="underline font-semibold">AI Job Matcher</a>, and <a href="/job-alerts" class="underline font-semibold">set up job alerts</a> at no cost. We only charge companies to post listings.'
    },
    {
      question: "How do I post a job for my team?",
      answer:
        'Use <a href="/post-job" class="underline font-semibold">Post a Role</a> to enter your role details—your listing will be live within minutes.'
    },
    {
      question: "Does the AI Matcher recognize specific tools like Figma, Rive, or Framer?",
      answer:
        'Yes. Our engine is trained on modern design stacks and identifies proficiency in Design Systems, Prototyping, and Motion Design. <a href="/ai-job-matcher" class="underline font-semibold">Try the AI Job Matcher</a> to match with teams that value specific tool mastery.'
    },
    {
      question: "Can I filter jobs by specific design disciplines like 'UX Research' or 'Design Ops'?",
      answer:
        'Absolutely. We\'ve moved beyond generic titles. <a href="/jobs" class="underline font-semibold">Browse jobs</a> and drill down into niches including Service Design, UX Writing, and Design Operations, or filter by <a href="/jobs/ux-research" class="underline font-semibold">UX Research</a> and <a href="/jobs/design-engineering" class="underline font-semibold">Design Engineering</a> to find the right fit.'
    },
    {
      question: "Do you provide salary benchmarks for the design roles listed?",
      answer:
        'We provide transparency wherever possible. Many listings include verified salary ranges. Our <a href="/salary-guide" class="underline font-semibold">Salary Guide</a> provides data on design compensation across major markets and remote roles.'
    },
    {
      question: "How can I stay updated on the latest design hiring trends?",
      answer:
        'Beyond our job board, we share insights on design hiring trends. <a href="/blog" class="underline font-semibold">Read our blog</a> for tool adoption trends (e.g. AI-augmented design) and shifts in the remote vs. hybrid landscape.'
    },
    {
      question: "Is my resume data safe when using the AI Matcher?",
      answer:
        'Your privacy is our priority. We use encrypted, localized processing for resume analysis. Your data is only used for match scores and is never sold or used to train public AI models. <a href="/ai-job-matcher" class="underline font-semibold">Learn more about the AI Matcher</a> or <a href="/privacy" class="underline font-semibold">our privacy policy</a>.'
    }
  ];

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document: Document,
    private supabaseService: SupabaseService,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    private seoService: SeoService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    // Anchor the homepage's canonical so query-string variants
    // (?utm_source=…, ?ref=…) don't get treated as duplicate URLs by Google.
    // Added May 25, 2026 as part of the SEO sweep.
    this.seoService.setCanonicalUrl("https://designjobs.careers/");

    if (this.isBrowser) {
      this.authService.authInitialized$
        .pipe(
          filter((ready) => ready),
          take(1)
        )
        .subscribe(() => {
          if (
            this.authService.isAuthenticated() &&
            this.router.url === '/'
          ) {
            this.router.navigate(["/jobs"]);
          }
        });
    }
  }

  /**
   * PostHog: capture homepage event with viewport and device context.
   */
  captureHome(
    eventName: string,
    properties?: Record<string, unknown>
  ): void {
    if (
      !this.isBrowser ||
      typeof posthog === "undefined" ||
      typeof posthog.capture !== "function"
    )
      return;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const deviceSize =
      viewportWidth < 768
        ? "mobile"
        : viewportWidth < 1024
          ? "tablet"
          : "desktop";
    posthog.capture(eventName, {
      ...properties,
      viewport_width: viewportWidth,
      viewport_height: viewportHeight,
      device_size: deviceSize,
      page: "home",
    });
  }

  /** Template-friendly: track click from hero, toolkit cards, etc. */
  trackHomeClick(eventName: string, properties?: Record<string, unknown>): void {
    this.captureHome(eventName, properties);
  }

  /** Navigate to profile and track; used by custom profile card (div, not anchor). */
  openProfileCard(): void {
    this.trackHomeClick("home_toolkit_card_clicked", { card: "custom_profile" });
    this.router.navigate(["/profile"]);
  }

  /** Resume analyser feature modal */
  showResumeAnalyserModal = false;

  /** Example report modal (same as resume results modal with sample data) */
  showExampleReportModal = false;

  readonly exampleResumeReport: ResumeAnalysisResult = {
    jobTitle: "Product Designer",
    company: "United Tech",
    resumeFileName: "EvanSharp_Resume.pdf",
    resumeSource: "uploaded",
    atsScore: 85,
    keywordMatchScore: 82,
    experienceMatchScore: 90,
    skillsMatchScore: 80,
    overview:
      "Your resume showcases a strong background in <highlight>product " +
      "design</highlight> with over 12 years of experience, including " +
      "<highlight>Figma</highlight> proficiency and a solid understanding " +
      "of <highlight>UX principles</highlight>. However, there are some " +
      "areas where you can enhance your alignment with the job description.",
    stats: {
      actionRequiredCount: 2,
      passedChecksCount: 6,
      experiencesMatchPercent: 90,
      skillsMatchPercent: 80,
      experiencesMatched: 9,
      experiencesTotal: 10,
      skillsMatched: 12,
      skillsTotal: 14,
    },
    doingWell: [
      "Strong experience in product design with over 12 years in the field",
      "Proficiency in Figma and design systems",
      "Experience designing complex user flows",
    ],
    actionRequired: [
      {
        category: "Keywords",
        priority: "high",
        description:
          "Your resume lacks the specific term business requirements, " +
          "which is crucial for this role.",
        suggestion:
          "Add a sentence that includes your experience working with " +
          "business requirements in your projects.",
      },
      {
        category: "Skills",
        priority: "medium",
        description:
          "You did not explicitly mention communication and presentation " +
          "skills, which are essential for this position.",
        suggestion:
          "Include a bullet point in your skills section that highlights " +
          "your communication and presentation skills.",
      },
    ] as ActionItem[],
    additionalTips: [
      {
        category: "Formatting",
        priority: "low",
        description:
          "Consider improving the overall structure of your resume for " +
          "better readability.",
        suggestion:
          "Use consistent formatting for headings and bullet points to " +
          "enhance clarity.",
      },
      {
        category: "Achievements",
        priority: "low",
        description:
          "Quantifying your achievements can strengthen your resume.",
        suggestion:
          "Include metrics such as user engagement or satisfaction rates " +
          "in your project descriptions.",
      },
    ] as TipItem[],
  };

  readonly exampleJob: Job = {
    title: "Product Designer",
    company: "United Tech",
    location: "Remote",
    employment_type: "Full-time",
    level: "Senior",
    category: "Product Design",
    work_type: WorkType.Remote,
    salary_min: null,
    salary_max: null,
    short_description: "",
    about_company: "",
    responsibilities: [],
    requirements: [],
    benefits: [],
    application_url: "/jobs",
  };

  openResumeAnalyserModal(): void {
    this.trackHomeClick("home_toolkit_card_clicked", { card: "resume_analyser" });
    this.showResumeAnalyserModal = true;
    this.setBodyScrollLock(true);
    setTimeout(() => this.resumeAnalyserVideo?.nativeElement?.play(), 100);
  }

  onResumeVideoCanPlay(event: Event): void {
    if (this.showResumeAnalyserModal) {
      (event.target as HTMLVideoElement).play();
    }
  }

  onResumeAnalyserModalChange(open: boolean): void {
    this.showResumeAnalyserModal = open;
    this.setBodyScrollLock(open);
    if (!open) {
      const video = this.resumeAnalyserVideo?.nativeElement;
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    }
  }

  private setBodyScrollLock(lock: boolean): void {
    if (!this.isBrowser) return;
    const { body, documentElement } = this.document;
    if (lock) {
      body.style.overflow = "hidden";
      documentElement.style.overflow = "hidden";
    } else {
      body.style.overflow = "";
      documentElement.style.overflow = "";
    }
  }

  closeResumeAnalyserModal(): void {
    this.showResumeAnalyserModal = false;
  }

  openExampleReportModal(): void {
    this.showExampleReportModal = true;
  }

  closeExampleReportModal(): void {
    this.showExampleReportModal = false;
    if (!this.showResumeAnalyserModal) {
      this.setBodyScrollLock(false);
    }
  }

  async selectCategory(categoryName: string): Promise<void> {
    if (this.selectedCategory === categoryName) return;
    this.selectedCategory = categoryName;
    this.triggerCategoryIconAnimation(categoryName);
    this.captureHome("home_category_selected", { category: categoryName });

    const cached = this.jobsByCategory.get(categoryName);
    if (cached !== undefined) {
      this.jobs = cached;
      this.jobsError = null;
      this.cdr.detectChanges();
      setTimeout(() => this.scrollCategoriesNavToSelected(), 0);
      return;
    }
    await this.loadJobsForCategory(categoryName);
    setTimeout(() => this.scrollCategoriesNavToSelected(), 0);
  }

  selectPreviousCategory(): void {
    const idx = this.categories.findIndex(
      (c) => c.name === this.selectedCategory
    );
    if (idx <= 0) return;
    this.captureHome("home_ecosystem_arrow_clicked", {
      direction: "previous",
      from_category: this.selectedCategory,
    });
    this.selectCategory(this.categories[idx - 1].name);
  }

  selectNextCategory(): void {
    const idx = this.categories.findIndex(
      (c) => c.name === this.selectedCategory
    );
    if (idx < 0 || idx >= this.categories.length - 1) return;
    this.captureHome("home_ecosystem_arrow_clicked", {
      direction: "next",
      from_category: this.selectedCategory,
    });
    this.selectCategory(this.categories[idx + 1].name);
  }

  /**
   * On mobile, scroll the categories nav so the selected category is centered.
   */
  scrollCategoriesNavToSelected(): void {
    if (!this.isBrowser || !this.categoriesNavRef?.nativeElement) return;
    const nav = this.categoriesNavRef.nativeElement;
    if (window.innerWidth >= 1024) return;
    const selectedBtn = nav.querySelector<HTMLElement>(
      `[data-category="${this.selectedCategory}"]`
    );
    if (!selectedBtn) return;
    const target =
      selectedBtn.offsetLeft -
      nav.clientWidth / 2 +
      selectedBtn.offsetWidth / 2;
    const maxScroll = nav.scrollWidth - nav.clientWidth;
    const scrollLeft = Math.max(0, Math.min(target, maxScroll));
    nav.scrollTo({ left: scrollLeft, behavior: "smooth" });
  }

  async loadJobsForCategory(categoryName: string): Promise<void> {
    if (!this.isBrowser) return;

    // Check static cache first (persists across navigations)
    const cached = HomepageNewComponent.jobsByCategoryCache.get(categoryName);
    if (HomepageNewComponent.isCacheValid(cached || null)) {
      this.jobs = cached!.data;
      this.jobsByCategory.set(categoryName, this.jobs);
      this.addJobPostingSchema(this.jobs);
      this.cdr.detectChanges();
      return;
    }

    // Also check instance cache (for quick tab switches within same session)
    if (this.jobsByCategory.has(categoryName)) {
      this.jobs = this.jobsByCategory.get(categoryName)!;
      this.addJobPostingSchema(this.jobs);
      this.cdr.detectChanges();
      return;
    }

    this.isLoadingJobs = true;
    this.jobsError = null;
    this.cdr.detectChanges();

    try {
      const { data, error } = await this.supabaseService
        .getClient()
        .from('jobs')
        .select(`
          id,
          title,
          company,
          category,
          work_type,
          employment_type,
          created_at,
          logo_img,
          job_locations(
            city_geocoded,
            country_geocoded,
            state_geocoded,
            location_formatted
          )
        `)
        .eq('status', 'published')
        .eq('is_expired', false)
        .ilike('category', `%${categoryName}%`)
        .order('created_at', { ascending: false })
        .limit(12);

      if (error) throw error;

      this.jobs = (data || []).map(job => ({
        ...job,
        display_location: this.formatJobLocation(job.job_locations, job.work_type),
      })) as Job[];

      // Update both caches
      this.jobsByCategory.set(categoryName, this.jobs);
      HomepageNewComponent.jobsByCategoryCache.set(categoryName, {
        data: this.jobs,
        timestamp: Date.now(),
      });
      
      this.addJobPostingSchema(this.jobs);
    } catch (error) {
      console.error('Error loading jobs:', error);
      this.jobsError = 'Failed to load jobs. Please try again.';
      this.jobs = [];
    } finally {
      this.isLoadingJobs = false;
      this.cdr.detectChanges();
    }
  }

  private formatJobLocation(locations: any[], workType?: string): string {
    if (!locations || locations.length === 0) {
      return workType === WorkType.Remote ? 'Remote' : '';
    }

    const distinctLocationStrings = new Set<string>();
    locations.forEach((loc: any) => {
      if (loc.location_formatted) {
        distinctLocationStrings.add(loc.location_formatted);
      } else if (loc.city_geocoded && loc.country_geocoded) {
        distinctLocationStrings.add(`${loc.city_geocoded}, ${loc.country_geocoded}`);
      } else if (loc.state_geocoded && loc.country_geocoded) {
        distinctLocationStrings.add(`${loc.state_geocoded}, ${loc.country_geocoded}`);
      } else if (loc.city_geocoded) {
        distinctLocationStrings.add(loc.city_geocoded);
      } else if (loc.state_geocoded) {
        distinctLocationStrings.add(loc.state_geocoded);
      } else if (loc.country_geocoded) {
        distinctLocationStrings.add(loc.country_geocoded);
      }
    });

    if (distinctLocationStrings.size === 0) {
      return workType === WorkType.Remote ? 'Remote' : '';
    }

    const locationStr = Array.from(distinctLocationStrings).join('; ');
    if (workType === WorkType.Remote) {
      return `Remote (${locationStr})`;
    }
    return locationStr;
  }

  viewAllJobs(source: "floating" | "list" = "floating"): void {
    this.captureHome("home_view_all_jobs_clicked", {
      source,
      category: this.selectedCategory,
    });
    const category = this.categories.find(
      (c) => c.name === this.selectedCategory
    );
    const categorySlug =
      category?.id ??
      this.selectedCategory
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/\//g, '-');
    
    // On mobile, navigate directly instead of opening in new tab
    if (this.isBrowser && window.innerWidth < 768) {
      this.router.navigate(['/jobs', categorySlug]);
    } else {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/jobs', categorySlug])
      );
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  /** Same logic as browse-jobs / job-list-display for relative posted time */
  formatJobPostedTime(createdAt?: string): string {
    if (!createdAt) return "";

    const now = new Date();
    const jobDate = new Date(createdAt);

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    if (jobDate >= startOfToday) return "Today";
    if (jobDate >= startOfYesterday) return "Yesterday";

    const diffMs = now.getTime() - jobDate.getTime();
    const diffDay = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeek = Math.floor(diffDay / 7);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear = Math.floor(diffDay / 365);

    if (diffYear > 0) return `${diffYear}y`;
    if (diffMonth > 0) return `${diffMonth}mo`;
    if (diffWeek > 0) return `${diffWeek}w`;
    if (diffDay >= 60) return "60+ days ago";
    if (diffDay > 0) return `${diffDay}d`;
    return "Today";
  }

  ngAfterViewInit(): void {
    // Schema injection runs on both SSR and browser (renderer + this.document)
    this.addFounderSchema();
    this.addFaqSchema();

    if (this.isBrowser) {
      this.captureHome("home_page_viewed");
      this.initLenisCarousel();
      setTimeout(() => this.loadJobsForCategory(this.selectedCategory), 0);
      setTimeout(() => this.loadFeaturedCompanies(), 0);
      setTimeout(() => this.loadHiringAnnouncements(), 0);
      setTimeout(() => this.loadBlogPosts(), 0);
    }
  }

  ngOnDestroy(): void {
    if (this.showResumeAnalyserModal || this.showExampleReportModal) {
      this.setBodyScrollLock(false);
    }
    if (this.categoryIconAnimationTimer) {
      clearTimeout(this.categoryIconAnimationTimer);
      this.categoryIconAnimationTimer = null;
    }
    const wrapper = this.toolkitCardsScroll?.nativeElement;
    if (wrapper && this.toolkitScrollListener) {
      wrapper.removeEventListener("scroll", this.toolkitScrollListener);
      this.toolkitScrollListener = null;
    }
    const insightsEl = this.insightsCardsScroll?.nativeElement;
    if (insightsEl && this.insightsScrollListener) {
      insightsEl.removeEventListener("scroll", this.insightsScrollListener);
      this.insightsScrollListener = null;
    }
    if (this.resizeListener) {
      window.removeEventListener("resize", this.resizeListener);
      this.resizeListener = null;
    }
    this.lenisCarousel?.destroy();
    this.lenisCarousel = null;
    this.removeJobPostingSchema();
    this.removeFounderSchema();
    this.removeFaqSchema();
  }

  private triggerCategoryIconAnimation(categoryName: string): void {
    if (!this.isBrowser) return;

    this.animatingCategory = categoryName;
    if (this.categoryIconAnimationTimer) {
      clearTimeout(this.categoryIconAnimationTimer);
    }

    this.categoryIconAnimationTimer = setTimeout(() => {
      this.animatingCategory = null;
      this.categoryIconAnimationTimer = null;
      this.cdr.detectChanges();
    }, 950);
  }

  toggleFoundersNote(): void {
    this.foundersNoteExpanded = !this.foundersNoteExpanded;
  }

  private static readonly FOUNDER_SCHEMA_ID = "home-founder-schema";

  private addFounderSchema(): void {
    const head = this.document.head;
    if (!head) return;

    const existing = this.document.getElementById(
      HomepageNewComponent.FOUNDER_SCHEMA_ID
    );
    if (existing) existing.remove();

    const schema = {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Carlos Palma",
      "jobTitle": "Founder",
      "url": "https://linkedin.com/in/cpalma1993",
      "sameAs": ["https://linkedin.com/in/cpalma1993"],
      "worksFor": {
        "@type": "Organization",
        "name": "Designjobs",
        "url": "https://designjobs.careers",
        "founder": {
          "@type": "Person",
          "name": "Carlos Palma"
        }
      }
    };

    const script = this.renderer.createElement("script");
    if (!script) return;
    script.id = HomepageNewComponent.FOUNDER_SCHEMA_ID;
    script.type = "application/ld+json";
    script.text = JSON.stringify(schema);
    head.appendChild(script);
    this.founderSchemaScript = script;
  }

  private removeFounderSchema(): void {
    const existing = this.document.getElementById(
      HomepageNewComponent.FOUNDER_SCHEMA_ID
    );
    if (existing) existing.remove();
    this.founderSchemaScript = null;
  }

  toggleFaq(index: number): void {
    const expanding = !this.expandedFaqs.has(index);
    if (this.expandedFaqs.has(index)) {
      this.expandedFaqs.delete(index);
    } else {
      this.expandedFaqs.add(index);
    }
    this.captureHome("home_faq_toggled", {
      faq_index: index,
      question: this.faqs[index]?.question?.slice(0, 80),
      expanded: expanding,
    });
  }

  private static readonly FAQ_SCHEMA_ID = "home-faq-schema";

  private addFaqSchema(): void {
    const head = this.document.head;
    if (!head) return;

    const existing = this.document.getElementById(
      HomepageNewComponent.FAQ_SCHEMA_ID
    );
    if (existing) existing.remove();

    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "name": "Frequently asked questions – DesignJobs.careers",
      "mainEntity": this.faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": this.stripFaqHtml(faq.answer)
        }
      }))
    };

    const script = this.renderer.createElement("script");
    if (!script) return;
    script.id = HomepageNewComponent.FAQ_SCHEMA_ID;
    script.type = "application/ld+json";
    script.text = JSON.stringify(schema);
    head.appendChild(script);
    this.faqSchemaScript = script;
  }

  private removeFaqSchema(): void {
    const existing = this.document.getElementById(
      HomepageNewComponent.FAQ_SCHEMA_ID
    );
    if (existing) existing.remove();
    this.faqSchemaScript = null;
  }

  /** Strip HTML tags from FAQ answer for schema.org plain text. */
  private stripFaqHtml(html: string): string {
    return html.replace(/<[^>]*>/g, "").trim();
  }


  private initLenisCarousel(): void {
    const wrapper = this.toolkitCardsScroll?.nativeElement;
    const content = this.toolkitCardsGrid?.nativeElement;
    if (!wrapper || !content) return;

    this.lenisCarousel = new Lenis({
      wrapper,
      content,
      orientation: "horizontal",
      gestureOrientation: "horizontal", /* Only react to horizontal scroll, not vertical */
      autoRaf: true,
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });

    this.updateToolkitScrollState();
    this.toolkitScrollListener = () => this.updateToolkitScrollState();
    wrapper.addEventListener("scroll", this.toolkitScrollListener, {
      passive: true,
    });

    this.resizeListener = () => {
      this.updateToolkitScrollState();
      this.updateInsightsScrollState();
    };
    window.addEventListener("resize", this.resizeListener);

    setTimeout(() => this.updateToolkitScrollState(), 100);
    this.initInsightsScrollState();
  }

  private initInsightsScrollState(): void {
    const el = this.insightsCardsScroll?.nativeElement;
    if (!el) return;
    this.updateInsightsScrollState();
    this.insightsScrollListener = () => this.updateInsightsScrollState();
    el.addEventListener("scroll", this.insightsScrollListener, {
      passive: true,
    });
    setTimeout(() => this.updateInsightsScrollState(), 100);
  }

  private updateToolkitScrollState(): void {
    const el = this.toolkitCardsScroll?.nativeElement;
    if (!el) return;
    const threshold = 2;
    const left = el.scrollLeft;
    const maxLeft = el.scrollWidth - el.clientWidth;
    this.toolkitCanScrollLeft = left > threshold;
    this.toolkitCanScrollRight = maxLeft > threshold && left < maxLeft - threshold;
    this.cdr.detectChanges();
  }

  private updateInsightsScrollState(): void {
    const el = this.insightsCardsScroll?.nativeElement;
    if (!el) return;
    const threshold = 2;
    const left = el.scrollLeft;
    const maxLeft = el.scrollWidth - el.clientWidth;
    this.insightsCanScrollLeft = left > threshold;
    this.insightsCanScrollRight =
      maxLeft > threshold && left < maxLeft - threshold;
    this.cdr.detectChanges();
  }

  scrollToolkitCarousel(direction: "left" | "right"): void {
    this.captureHome("home_toolkit_carousel_arrow_clicked", { direction });
    const el = this.toolkitCardsScroll?.nativeElement;
    if (!el) return;
    const delta = direction === "left" ? -this.scrollStep : this.scrollStep;
    const target = Math.max(0, el.scrollLeft + delta);

    if (this.lenisCarousel) {
      this.lenisCarousel.scrollTo(target, { immediate: false });
    } else {
      el.scrollTo({ left: target, behavior: "smooth" });
    }
    setTimeout(() => this.updateToolkitScrollState(), 400);
  }

  scrollInsights(direction: "left" | "right"): void {
    this.captureHome("home_insights_carousel_arrow_clicked", { direction });
    const el = this.insightsCardsScroll?.nativeElement;
    if (!el) return;
    const delta = direction === "left" ? -this.scrollStep : this.scrollStep;
    const target = Math.max(0, el.scrollLeft + delta);
    el.scrollTo({ left: target, behavior: "smooth" });
    setTimeout(() => this.updateInsightsScrollState(), 350);
  }

  async loadBlogPosts(): Promise<void> {
    // Check cache first
    if (HomepageNewComponent.isCacheValid(HomepageNewComponent.blogPostsCache)) {
      this.blogPosts = HomepageNewComponent.blogPostsCache!.data;
      return;
    }

    try {
      this.isBlogsLoading = true;
      const { data, error } = await this.supabaseService
        .getClient()
        .from("blog_posts")
        .select("*")
        .eq("visibility", true)
        .order("date", { ascending: false })
        .order("id", { ascending: false })
        .limit(8);

      if (error) {
        console.error("Error loading blog posts:", error);
        return;
      }

      this.blogPosts = data || [];

      // Cache the result
      HomepageNewComponent.blogPostsCache = {
        data: this.blogPosts,
        timestamp: Date.now(),
      };
    } catch (err) {
      console.error("Unexpected error loading blog posts:", err);
    } finally {
      this.isBlogsLoading = false;
    }
  }

  async loadFeaturedCompanies(): Promise<void> {
    if (!this.isBrowser) return;

    // Check cache first
    if (HomepageNewComponent.isCacheValid(HomepageNewComponent.featuredCompaniesCache)) {
      this.featuredCompanies = HomepageNewComponent.featuredCompaniesCache!.data;
      this.cdr.detectChanges();
      return;
    }
    
    this.isLoadingCompanies = true;
    
    try {
      const { data, error } = await this.supabaseService
        .getClient()
        .from('jobs')
        .select('company, logo_img')
        .eq('status', 'published')
        .eq('is_expired', false)
        .not('logo_img', 'is', null)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      const uniqueCompanies = new Map<string, string>();
      data?.forEach(job => {
        if (job.logo_img && !uniqueCompanies.has(job.company)) {
          uniqueCompanies.set(job.company, job.logo_img);
        }
      });

      this.featuredCompanies = Array.from(uniqueCompanies.entries())
        .slice(0, 5)
        .map(([company, logo_img]) => ({ company, logo_img }));

      // Cache the result
      HomepageNewComponent.featuredCompaniesCache = {
        data: this.featuredCompanies,
        timestamp: Date.now(),
      };

    } catch (error) {
      console.error('Error loading featured companies:', error);
    } finally {
      this.isLoadingCompanies = false;
      this.cdr.detectChanges();
    }
  }

  async loadHiringAnnouncements(): Promise<void> {
    if (!this.isBrowser) return;

    // Check cache first
    if (HomepageNewComponent.isCacheValid(HomepageNewComponent.hiringAnnouncementsCache)) {
      const cached = HomepageNewComponent.hiringAnnouncementsCache!.data;
      this.hiringAnnouncements = cached.map(job => ({
        id: job.id,
        company: job.company,
        logo_img: job.logo_img,
        category: this.cleanCategory(job.category),
        cleanTitle: this.cleanJobTitle(job.title, job.category),
      }));
      this.cdr.detectChanges();
      return;
    }

    try {
      const { data, error } = await this.supabaseService
        .getClient()
        .from('jobs')
        .select('id, company, logo_img, category, title')
        .eq('status', 'published')
        .eq('is_expired', false)
        .not('logo_img', 'is', null)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Cache the raw data
      HomepageNewComponent.hiringAnnouncementsCache = {
        data: data || [],
        timestamp: Date.now(),
      };

      this.hiringAnnouncements = (data || []).map(job => ({
        id: job.id,
        company: job.company,
        logo_img: job.logo_img,
        category: this.cleanCategory(job.category),
        cleanTitle: this.cleanJobTitle(job.title, job.category),
      }));

      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading hiring announcements:', error);
      this.hiringAnnouncements = [];
    }
  }

  onLogoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.style.display = 'none';
    }
  }

  private cleanCategory(category: string | null | undefined): string {
    if (category == null || category === '') {
      return '';
    }
    const categoryMap: Record<string, string> = {
      'product-design': 'Product Designer',
      'ui-ux-design': 'UX Designer',
      'ux-research': 'UX Researcher',
      'graphic-design': 'Graphic Designer',
      'design-engineering': 'Design Engineer',
      'web-design': 'Web Designer',
      'motion-design': 'Motion Designer',
    };

    const normalized = category.toLowerCase().replace(/\s+/g, '-');
    return categoryMap[normalized] ?? category;
  }

  private cleanJobTitle(
    title: string | null | undefined,
    category: string | null | undefined
  ): string {
    const safeTitle = title ?? '';
    const lowerTitle = safeTitle.toLowerCase();

    if (
      lowerTitle.includes('senior') ||
      lowerTitle.includes('sr.') ||
      lowerTitle.includes('sr ')
    ) {
      return 'Senior ' + this.cleanCategory(category);
    }

    if (
      lowerTitle.includes('junior') ||
      lowerTitle.includes('jr.') ||
      lowerTitle.includes('jr ')
    ) {
      return 'Junior ' + this.cleanCategory(category);
    }

    if (lowerTitle.includes('lead')) {
      return 'Lead ' + this.cleanCategory(category);
    }

    if (lowerTitle.includes('principal')) {
      return 'Principal ' + this.cleanCategory(category);
    }

    if (lowerTitle.includes('staff')) {
      return 'Staff ' + this.cleanCategory(category);
    }

    return this.cleanCategory(category);
  }

  private addJobPostingSchema(jobs: Job[]): void {
    const head = this.document.head;
    if (!head) return;

    this.removeJobPostingSchema();

    const jobSchemas = jobs
      .filter((job) => job.id && job.title && job.company)
      .map((job) => this.buildJobPostingSchema(job));

    const script = this.renderer.createElement("script");
    script.type = "application/ld+json";
    script.id = "homepage-jobs-schema";
    script.text = JSON.stringify(jobSchemas);
    this.renderer.appendChild(head, script);
  }

  /** Build a single JobPosting schema (aligned with browse-jobs). */
  private buildJobPostingSchema(job: Job): Record<string, unknown> {
    const address: Record<string, string> = { "@type": "PostalAddress" };
    let hasCountry = false;

    if (job.job_locations?.length) {
      const first = job.job_locations[0];
      if (first.country_geocoded) {
        address["addressCountry"] = first.country_geocoded;
        hasCountry = true;
      }
      address["addressLocality"] = first.city_geocoded || "Anywhere";
      address["addressRegion"] = first.state_geocoded || "Anywhere";
      address["postalCode"] = first.postal_code_geocoded || "Anywhere";
      if (first.raw_location_string) {
        address["streetAddress"] = first.raw_location_string;
      }
    }

    const isRemoteOrHybrid =
      job.work_type === WorkType.Remote || job.work_type === WorkType.Hybrid;
    if (isRemoteOrHybrid) {
      if (!address["addressLocality"]) {
        address["addressLocality"] =
          job.work_type === WorkType.Remote ? "Remote" : "Hybrid";
      }
      if (!hasCountry) {
        address["addressCountry"] = "Anywhere";
        hasCountry = true;
      }
    } else if (!hasCountry && (job.display_location || job.location)) {
      const raw = (job.display_location || job.location || "")
        .split(",")
        .map((p) => p.trim());
      if (raw.length >= 2) {
        address["addressCountry"] = raw[raw.length - 1];
        hasCountry = true;
      }
      if (raw.length >= 1) address["addressLocality"] = raw[0];
    }

    if (!hasCountry) address["addressCountry"] = "Anywhere";
    if (!address["addressLocality"]) address["addressLocality"] = "Anywhere";
    if (!address["addressRegion"]) address["addressRegion"] = "Anywhere";
    if (!address["postalCode"]) address["postalCode"] = "Anywhere";
    if (!address["streetAddress"]) address["streetAddress"] = "Anywhere";

    const schema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: job.title,
      description: this.sanitizeDescriptionForSchema(
        job.full_description ||
          job.short_description ||
          "Design job opportunity"
      ),
      datePosted: job.created_at || new Date().toISOString(),
      validThrough:
        job.application_deadline ||
        new Date(
          new Date(job.created_at || Date.now()).getTime() +
            90 * 24 * 60 * 60 * 1000
        ).toISOString(),
      hiringOrganization: {
        "@type": "Organization",
        name: job.company,
        ...(job.company_website && {
          url: this.formatCompanyUrl(job.company_website),
        }),
        ...(job.logo_img && { logo: job.logo_img }),
      },
      identifier: {
        "@type": "PropertyValue",
        name: "Job ID",
        value: job.id ?? "",
      },
      industry: "Design",
      directApply: job.application_url?.startsWith("mailto:") ?? false,
      jobLocation: [{ "@type": "Place", address }],
      url: `https://designjobs.careers/job/${job.id}`,
    };

    if (job.employment_type) {
      schema["employmentType"] = this.mapEmploymentType(job.employment_type);
    }

    if (isRemoteOrHybrid) {
      schema["jobLocationType"] = "TELECOMMUTE";
      const uniqueCountries = new Set<string>();
      job.job_locations?.forEach((loc) => {
        if (loc.country_geocoded) uniqueCountries.add(loc.country_geocoded);
      });
      if (uniqueCountries.size === 0 && address["addressCountry"]) {
        uniqueCountries.add(address["addressCountry"]);
      }
      if (uniqueCountries.size === 0) {
        schema["applicantLocationRequirements"] = [
          { "@type": "Country", name: "Anywhere" },
        ];
      } else if (uniqueCountries.size === 1) {
        schema["applicantLocationRequirements"] = [
          {
            "@type": "Country",
            name: Array.from(uniqueCountries)[0],
          },
        ];
      } else {
        schema["applicantLocationRequirements"] = [
          {
            "@type": "Place",
            description: `May be performed remotely from ${Array.from(uniqueCountries).join(" or ")}.`,
          },
        ];
      }
    }

    if (job.salary_min != null || job.salary_max != null) {
      schema["baseSalary"] = {
        "@type": "MonetaryAmount",
        currency: job.salary_currency || "USD",
        value: {
          "@type": "QuantitativeValue",
          ...(job.salary_min != null && { minValue: job.salary_min }),
          ...(job.salary_max != null && { maxValue: job.salary_max }),
          unitText: this.getSalaryUnitText(job.salary_min, job.salary_max),
        },
      };
    }

    if (
      job.responsibilities?.length &&
      job.responsibilities.some((r) => r?.trim())
    ) {
      schema["responsibilities"] = job.responsibilities
        .filter((r) => r?.trim())
        .join("; ");
    }
    if (
      job.requirements?.length &&
      job.requirements.some((r) => r?.trim())
    ) {
      schema["qualifications"] = job.requirements
        .filter((r) => r?.trim())
        .join("; ");
    }
    if (job.skills?.length && job.skills.some((s) => s?.trim())) {
      schema["skills"] = job.skills.filter((s) => s?.trim()).join(", ");
    }
    if (job.benefits?.length && job.benefits.some((b) => b?.trim())) {
      schema["jobBenefits"] = job.benefits
        .filter((b) => b?.trim())
        .join(", ");
    }

    return schema;
  }

  private sanitizeDescriptionForSchema(description: string): string {
    if (!description) return "";
    let text = description.replace(/<[^>]*>/g, "");
    text = text
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&nbsp;/g, " ");
    text = text
      .replace(/\n/g, " ")
      .replace(/['"'\\]/g, "")
      .replace(/\s+/g, " ")
      .trim();
    return text.slice(0, 5000);
  }

  private getSalaryUnitText(
    salaryMin: number | null | undefined,
    salaryMax: number | null | undefined
  ): string {
    const v = salaryMax ?? salaryMin;
    if (v == null) return "YEAR";
    if (
      (salaryMin != null && salaryMin < 10000) ||
      (salaryMax != null && salaryMax < 10000)
    ) {
      return "HOUR";
    }
    return "YEAR";
  }

  private formatCompanyUrl(url?: string): string {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `https://${url}`;
  }

  private removeJobPostingSchema(): void {
    const existingScript = this.document.getElementById("homepage-jobs-schema");
    if (existingScript) {
      existingScript.remove();
    }
  }

  private mapEmploymentType(type: string): string {
    const mapping: { [key: string]: string } = {
      "Full-time": "FULL_TIME",
      "Part-time": "PART_TIME",
      "Contract": "CONTRACTOR",
      "Temporary": "TEMPORARY",
      "Internship": "INTERN",
    };
    return mapping[type] || "FULL_TIME";
  }
}
